import { trackAmplitudeEvent } from "../../amplitude";
import {
  buildPlanRows,
  distributeNewWordsTotal,
  distributeReviewsTotal,
  reorderSection,
  setCollectionPlayOptions,
  toggleCollection,
  toggleSection
} from "./todaysPlanService";

const trackConfigSaved = (properties) => trackAmplitudeEvent("todays_plan_config_saved", properties);

const UNEVEN_SPLIT_NOTICES = {
  new_words: "Collections with fewer new words left take fewer, so the rest went to your other collections.",
  review: "Collections with fewer reviews ready take fewer, so the rest went to your other collections."
};

export default function useTodaysPlanConfig({ collections, config, onConfigChange, onNotice = () => {}, queue }) {
  const { newWordsRows, reviewRows } = buildPlanRows(config, collections, queue);
  const rowsFor = (type) => type === "review" ? reviewRows : newWordsRows;
  const collectionName = (collectionId) => (collections.find((c) => String(c.id) === collectionId) || {}).name;

  const toggleAll = (type) => {
    const isEnabling = !rowsFor(type).every((row) => row.enabled);
    onConfigChange(toggleSection(config, rowsFor(type).map((row) => row.collectionId), type, isEnabling));
    trackConfigSaved({ toggled_action: `${isEnabling ? "enable" : "disable"}_all_${type === "review" ? "reviews" : "new_words"}` });
  };

  const toggleRow = (row) => {
    onConfigChange(toggleCollection(config, row.collectionId, row.type));
    trackConfigSaved({ collection_name: row.name, toggled_action: "toggle_collection" });
  };

  const reorderRow = (type, index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if(targetIndex < 0 || targetIndex >= rowsFor(type).length) return;
    onConfigChange(reorderSection(config, rowsFor(type).map((row) => row.collectionId), type, index, direction));
    trackConfigSaved({ direction, toggled_action: "reorder" });
  };

  const moveSection = (type, direction) => {
    const newWordsFirst = type === "new_words" ? direction === "up" : direction === "down";
    if(newWordsFirst === config.newWordsFirst) return;
    onConfigChange({ ...config, newWordsFirst });
    trackConfigSaved({ new_words_first: newWordsFirst, toggled_action: "reorder_sections" });
  };

  const noticeUnevenSplit = (updated, type, total) => {
    const enabledRows = rowsFor(type).filter((row) => row.enabled);
    const field = type === "review" ? "reviewsPerDay" : "newWordsPerDay";
    const isCapped = (row) => {
      const cc = updated.collections.find((u) => u.collectionId === row.collectionId);
      return row.capacity < total / enabledRows.length && ((cc && cc[field]) || 0) >= row.capacity;
    };
    if(enabledRows.length > 1 && enabledRows.some(isCapped)) onNotice(UNEVEN_SPLIT_NOTICES[type]);
  };

  // Applies both totals to one config so the second doesn't overwrite the first.
  const setSectionTotals = ({ newWords, reviews }) => {
    let updated = config;
    if(reviews !== undefined) updated = distributeReviewsTotal(updated, collections, reviews);
    if(newWords !== undefined) updated = distributeNewWordsTotal(updated, collections, newWords);
    if(updated === config) return;
    onConfigChange(updated);
    if(reviews !== undefined) {
      noticeUnevenSplit(updated, "review", reviews);
      trackConfigSaved({ toggled_action: "set_total_reviews", value: reviews });
    }
    if(newWords !== undefined) {
      noticeUnevenSplit(updated, "new_words", newWords);
      trackConfigSaved({ toggled_action: "set_total_new_words", value: newWords });
    }
  };

  const setCollectionCount = (type, collectionId, count) => {
    const field = type === "review" ? "reviewsPerDay" : "newWordsPerDay";
    onConfigChange(setCollectionPlayOptions(config, collectionId, { [field]: count }));
    trackConfigSaved({
      collection_name: collectionName(collectionId),
      toggled_action: type === "review" ? "set_collection_reviews" : "set_collection_new_words",
      value: count
    });
  };

  const updatePlayOptions = (collectionId, updates) => {
    onConfigChange(setCollectionPlayOptions(config, collectionId, updates));
    const [option] = Object.keys(updates);
    trackConfigSaved({ collection_name: collectionName(collectionId), option, toggled_action: "update_play_options", value: updates[option] });
  };

  const applyPlayStyle = ({ appliesTo, mode, roundLength, skill }) => {
    const appliesToReviews = appliesTo === "review" || appliesTo === "both";
    const appliesToNewWords = appliesTo === "new_words" || appliesTo === "both";
    onConfigChange({
      ...config,
      collections: config.collections.map((cc) => ({
        ...cc,
        ...(appliesToReviews ? { reviewMode: mode, reviewRoundLength: roundLength, reviewSkill: skill } : {}),
        ...(appliesToNewWords ? { newWordsMode: mode, newWordsRoundLength: roundLength, newWordsSkill: skill } : {})
      }))
    });
    trackConfigSaved({ applies_to: appliesTo, mode, round_length: roundLength, skill, toggled_action: "apply_default_play_style" });
  };

  return {
    applyPlayStyle,
    moveSection,
    newWordsRows,
    reorderRow,
    reviewRows,
    setCollectionCount,
    setSectionTotals,
    toggleAll,
    toggleRow,
    updatePlayOptions
  };
}
